import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import DashboardPage from './pages/DashboardPage';
import { AssignmentsPage } from './pages/AssignmentsPage';
import { RatingPage } from './pages/RatingPage';
import { TeamAssignmentsPage } from './pages/TeamAssignmentsPage';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { TutorialProvider, useTutorial } from './components/TutorialProvider';
import AdminPage from './pages/AdminPage';
import KpiManagementPage from './pages/KpiManagementPage';
import IncentiveManagementPage from './pages/IncentiveManagementPage';
import HrDetailsPage from './pages/HrDetailsPage';
import LeaveManagementPage from './pages/LeaveManagementPage';
import LeaveApplicationPage from './pages/LeaveApplicationPage';
import SettingsPage from './pages/SettingsPage';
import MetricsSettingsPage from './pages/MetricsSettingsPage';
import { ToastProvider } from './components/Toast';
import './styles.css';
import { PeriodProvider } from './context/PeriodContext';

function AdminDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="admin-dropdown">
      <button
        onClick={() => setOpen(!open)}
        className="admin-dropdown-trigger"
      >
        Admin ▾
      </button>
      {open && (
        <div className="admin-dropdown-menu">
          <Link to="/admin" onClick={() => setOpen(false)}>Reindex</Link>
          <Link to="/kpis" onClick={() => setOpen(false)}>Manage KPIs</Link>
          <Link to="/incentives" onClick={() => setOpen(false)}>Incentives</Link>
          <Link to="/hr" onClick={() => setOpen(false)}>HR Details</Link>
          <Link to="/leave" onClick={() => setOpen(false)}>Leave</Link>
          <Link to="/settings" onClick={() => setOpen(false)}>Settings</Link>
          <Link to="/metrics" onClick={() => setOpen(false)}>Metrics</Link>
        </div>
      )}
    </div>
  );
}

function ViewAsSelector() {
  const stored = localStorage.getItem('roleOverride') || 'admin';
  const [value, setValue] = useState(stored);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 10 }}>
      <label style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 600 }}>View as:</label>
      <select
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          setValue(v);
          localStorage.setItem('roleOverride', v);
          window.location.reload();
        }}
        style={{
          fontSize: '0.75rem', padding: '2px 8px', borderRadius: 6,
          border: '1px solid #d0d5dd',
          background: value !== 'admin' ? '#fff3cd' : '#fff',
          fontWeight: value !== 'admin' ? 700 : 400,
        }}
      >
        <option value="admin">Admin</option>
        <option value="manager">Manager</option>
        <option value="user">User</option>
      </select>
    </div>
  );
}

function Shell() {
  const { login, logout, account, isManager, isAdmin, realAdmin, user, profileLoading } = useAuth();
  const { startTour } = useTutorial();
  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/dashboard" className="brand-lockup" aria-label="KPI Portal for Ancora">
          <img
            className="brand-lockup-img"
            src="/brand/kpi-portal-lockup.svg"
            alt="KPI Portal for Ancora"
            width={360}
            height={112}
          />
        </Link>
        <nav>
          <Link to="/dashboard">Dashboard</Link>
          <Link to="/assignments">Assignments</Link>
          <Link to="/leave-apply">Apply Leave</Link>
          {!profileLoading && (isManager || isAdmin) && <Link to="/leave">Leave Approvals</Link>}
          {!profileLoading && isManager && <Link to="/team">Team</Link>}
          {!profileLoading && isManager && !isAdmin && <Link to="/kpis">Manage KPIs</Link>}
          {!profileLoading && isAdmin && <AdminDropdown />}
          {!profileLoading && realAdmin && <ViewAsSelector />}
        </nav>
        <div className="auth">
          <button className="tour-help-btn" onClick={startTour} title="Start guided tour">?</button>
          {user ? (
            <>
              <span>Welcome, {user.displayName}</span>
              <button onClick={logout}>Logout</button>
            </>
          ) : account ? (
            <>
              <span>{account.username}</span>
              <button onClick={logout}>Logout</button>
            </>
          ) : (
            <>
              <span className="prelogin-strip">for Ancora</span>
              <button className="login-btn" onClick={login}>Login</button>
            </>
          )}
        </div>
      </header>
      <main>
        {profileLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>Loading…</p>
          </div>
        ) : (
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/assignments" element={<AssignmentsPage />} />
          <Route path="/team" element={<TeamAssignmentsPage />} />
          <Route path="/rating/:employeeUpn/:kpiCode/:periodId" element={<RatingPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/kpis" element={<KpiManagementPage />} />
          <Route path="/incentives" element={<IncentiveManagementPage />} />
          <Route path="/hr" element={<HrDetailsPage />} />
          <Route path="/leave" element={<LeaveManagementPage />} />
          <Route path="/leave-apply" element={<LeaveApplicationPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/metrics" element={<MetricsSettingsPage />} />
        </Routes>
        )}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <PeriodProvider>
          <TutorialProvider>
            <Shell />
          </TutorialProvider>
        </PeriodProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
